import { createSelector } from 'reselect';
import { flow, pipe } from 'fp-ts/function';
import { array, nonEmptyArray, eq, string, number, option } from 'fp-ts';
import { DateService, DateTimeService } from '@mews/framework';
import { ApplicationState, OccupancyData } from '@mews-commander/types';
import { Reservation } from '@mews-commander/data-access';
import { getItemByCommonPropertyValue } from '@mews/fp';
import { notNil } from '@mews-commander/utils';
import { canCurrentProfileOverrideRatePrice } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import {
    getAgeCategoryIdsByServiceId,
    getSortedAgeCategoriesByServiceId,
} from '@mews-commander/legacy-entities/src/ducks/ageCategories';
import { getCurrentEnterpriseAppDateTimeNow } from '@mews-commander/legacy-entities/src/selectors/session/dateTime';
import { getResourceCategory } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { getResourceCategoryIdByResourceId } from '@mews-commander/legacy-entities/src/ducks/resourceCategoryAssignments';
import { getSlotResourceCategoryId } from '@mews-commander/legacy-entities/src/ducks/resourceSlots';
import { getCurrentDateTimeLocalizationInfo } from '@mews-commander/core/legacy-globalization';
import { getCheckedReservations } from '../../selectors';
import { AgeCategoriesCountFormValues } from './AgeCategoriesCountForm/types';

export const getReservationPurposeFormInitialValues = flow(
    getCheckedReservations,
    getItemByCommonPropertyValue('purpose'),
    (reservationPurpose) => ({ reservationPurpose })
);

export const getSplitFormInitialValues = createSelector(
    getCurrentDateTimeLocalizationInfo,
    getCurrentEnterpriseAppDateTimeNow,
    (localizationInfo, dateTimeNow) => {
        const date = DateTimeService.toDate(dateTimeNow);
        return { date: DateService.toString(date, localizationInfo) };
    }
);

const createReservationPreselectedValueSelector = <TKey extends keyof Reservation>(key: TKey) =>
    createSelector(getCheckedReservations, getItemByCommonPropertyValue(key));
export const getPreselectedCompanyId = createReservationPreselectedValueSelector('partnerCompanyId');
export const getPreselectedTravelAgencyId = createReservationPreselectedValueSelector('travelAgencyId');
export const getPreselectedChannelNumber = createReservationPreselectedValueSelector('channelNumber');
export const getPreselectedRequestedCategoryId = createReservationPreselectedValueSelector('requestedCategoryId');
export const getPreselectedAdultCount = createReservationPreselectedValueSelector('adultCount');
export const getPreselectedChildCount = createReservationPreselectedValueSelector('childCount');

export const getResourceCategoryFormInitialValues = createSelector(
    getPreselectedRequestedCategoryId,
    canCurrentProfileOverrideRatePrice,
    (resourceCategoryId, canOverrideRatePrice) => ({
        resourceCategoryId,
        keepOldPrice: canOverrideRatePrice,
    })
);

export const getPersonMaxCount = (state: ApplicationState, { scope }: { scope: string }) =>
    pipe(
        getCheckedReservations(state, { scope }),
        array.map(({ assignedResourceId, requestedCategoryId, assignedSlotId, serviceId }) => {
            if (assignedResourceId) {
                const categoryId = getResourceCategoryIdByResourceId(state, {
                    resourceId: assignedResourceId,
                    serviceId,
                });
                if (categoryId) return getResourceCategory(state, { id: categoryId, serviceId });
            }
            if (assignedSlotId) {
                const categoryId = getSlotResourceCategoryId(state, { id: assignedSlotId });
                if (categoryId) return getResourceCategory(state, { id: categoryId, serviceId });
            }

            return getResourceCategory(state, { id: requestedCategoryId, serviceId });
        }),
        array.filterMap((category) =>
            category ? option.some(category.capacity + category.extraCapacity) : option.none
        ),
        (capacities) => Math.min.apply(null, capacities)
    );

export const getPersonCountFormInitialValues = createSelector(
    getPreselectedAdultCount,
    getPreselectedChildCount,
    (adultCount, childCount) => ({ adultCount, childCount, keepOldPrice: true })
);

export const showChannelNumber = createSelector(
    getPreselectedTravelAgencyId,
    getPreselectedChannelNumber,
    (travelAgencyId, channelNumber) => notNil(travelAgencyId) || notNil(channelNumber)
);

export const getTravelAgencyNumberFormInitialValues = createSelector(getPreselectedChannelNumber, (channelNumber) => ({
    channelNumber,
}));
export const getCompanyFormInitialValues = createSelector(getPreselectedCompanyId, (partnerCompanyId) => ({
    partnerCompanyId,
}));
export const getTravelAgencyFormInitialValues = createSelector(getPreselectedTravelAgencyId, (travelAgencyId) => ({
    travelAgencyId,
}));

const getCheckedReservationsWithCompleteOccupancyData = createSelector(
    getCheckedReservations,
    getAgeCategoryIdsByServiceId,
    (reservations, ageCategoryIds) => {
        const baseOccupancyData = pipe(
            ageCategoryIds,
            array.map((id) => ({ ageCategoryId: id, personCount: 0 }))
        );

        return pipe(
            reservations,
            array.map((reservation) => ({
                ...reservation,
                occupancyData: pipe(
                    reservation.occupancyData ?? [],
                    array.union<OccupancyData>(
                        eq.struct({
                            ageCategoryId: string.Eq,
                        })
                    )(baseOccupancyData)
                ),
            }))
        );
    }
);

export const getCheckedReservationCommonOccupancyData = createSelector(
    getCheckedReservationsWithCompleteOccupancyData,
    (reservations) => {
        const reservationCount = array.size(reservations);
        if (reservationCount === 0) {
            return [];
        } else if (array.isNonEmpty(reservations) && reservationCount === 1) {
            return pipe(reservations, nonEmptyArray.head, ({ occupancyData }) => occupancyData);
        }

        return pipe(
            reservations,
            array.map((reservation) => reservation.occupancyData),
            (occupancyData) =>
                array.isNonEmpty(occupancyData)
                    ? occupancyData.reduce(
                          array.intersection(
                              eq.struct({
                                  ageCategoryId: string.Eq,
                                  personCount: number.Eq,
                              })
                          )
                      )
                    : []
        );
    }
);

export const getSortedSelectedAgeCategories = createSelector(
    getSortedAgeCategoriesByServiceId,
    getCheckedReservationCommonOccupancyData,
    (allCategories, selectedOccupancyData) =>
        pipe(
            allCategories,
            array.filter(({ isDeleted, id }) => {
                if (!isDeleted) {
                    return true;
                }

                return pipe(
                    selectedOccupancyData,
                    array.findFirst(({ ageCategoryId }) => ageCategoryId === id),
                    option.match(
                        () => false,
                        ({ personCount }: OccupancyData) => personCount > 0
                    )
                );
            })
        )
);

export const getSortedSelectedAgeCategoryIds = createSelector(getSortedSelectedAgeCategories, (ageCategories) =>
    pipe(
        ageCategories,
        array.map(({ id }) => id)
    )
);

export const getAgeCategoriesCountFormInitialValues = createSelector(
    getSortedSelectedAgeCategoryIds,
    getCheckedReservationCommonOccupancyData,
    (categoryIds, occupancyData) =>
        pipe(
            categoryIds,
            array.reduce<string, AgeCategoriesCountFormValues>(
                { keepOldPrice: true, ageCategories: {} },
                (initialFormData, id) => ({
                    ...initialFormData,
                    ageCategories: {
                        ...initialFormData.ageCategories,
                        [id]: pipe(
                            occupancyData,
                            array.findFirst(({ ageCategoryId }) => ageCategoryId === id),
                            option.match(
                                () => 0,
                                ({ personCount }) => personCount
                            )
                        ),
                    },
                })
            )
        )
);
